import { transformCurriculumCourseTree } from 'layers/content/v3/transforms/transformCurriculumCourseTree'
import { selectors as lmsSelectors } from 'store/lmsManager'
import { countLessonsInNode } from 'utils/lmsHelpers'
import {
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/routing/routeHelpers'
import { K5_TRAINING_COURSE_CODE, MS_TRAINING_COURSE_CODE } from './constants'

export const getCompletedTrainingProgramNodesCount = data => {
  const trainingProgramNodes =
    data?.educator?.lesson_nodes?.filter(
      node =>
        node?.lesson_interaction_type === 'trainingProgram' && node?.is_done,
    ) || []

  return trainingProgramNodes.length
}

/**
 * Gets the appropriate instructor data based on the current URL context
 * @param {Object} state - Redux state
 * @returns {Object} The appropriate instructor data for the current program
 */
export const getInstructorDataForCurrentProgram = state => {
  if (isHighSchoolUrl()) {
    return lmsSelectors.selectHsInstructorData(state)
  } else if (isMiddleSchoolUrl()) {
    return lmsSelectors.selectMiddleSchoolInstructorData(state)
  } else if (isK5Url()) {
    return lmsSelectors.selectk5InstructorData(state)
  }
  return null
}

const countLegacyTrainingLeafNodes = courseTree => {
  const leafNodes = []

  const findLeafNodes = node => {
    if (!node) return

    if (node?.leaf === true) {
      leafNodes.push(node)
    }

    if (node?.children && node?.children?.length > 0) {
      for (const child of node?.children || []) {
        findLeafNodes(child)
      }
    }
  }

  findLeafNodes(courseTree)

  return leafNodes.filter(node => node?.requirementType !== 'choice').length
}

/**
 * Gets the total number of training leaf nodes in a course tree
 * @param {Object} courseTree - The course tree to analyze
 * @returns {number} The total number of required training leaf nodes
 */
export const getTotalTrainingNodesCount = courseTree => {
  if (!courseTree) {
    return 0
  }

  const curriculumLessonCount = countLessonsInNode(courseTree)
  if (curriculumLessonCount > 0) {
    return curriculumLessonCount
  }

  return countLegacyTrainingLeafNodes(courseTree)
}

const resolveTrainingTotalCount = ({
  trainingCourseTree,
  trainingArticleCount = 0,
}) => {
  const treeCount = getTotalTrainingNodesCount(trainingCourseTree)
  if (treeCount > 0) {
    return treeCount
  }

  return trainingArticleCount > 0 ? trainingArticleCount : 0
}

/**
 * Gets the number of completed training nodes for K8 programs
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {number} The number of completed training nodes
 */
export const getCompletedK8TrainingNodesCount = (
  instructorData = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  if (!instructorData?.educator) {
    return 0
  }

  let completedCount = 0

  if (Array.isArray(instructorData?.educator)) {
    const completedNodes = new Set()
    for (const registration of instructorData?.educator || []) {
      for (const lesson of registration?.lesson_nodes || []) {
        if (lesson && lesson?.is_done === true) {
          const courseNodeId = lesson?.leaf_node_id?.split('-')?.[0]
          if (courseNodeId) {
            completedNodes.add(courseNodeId)
          }
        }
      }
    }

    completedCount = completedNodes.size
  }

  return completedCount
}

/**
 * Formats the training progress string based on the program type
 * @param {number} completedCount - Number of completed training nodes
 * @param {number} totalCount - Total number of training nodes (for K8 only)
 * @param {boolean} isHighSchool - Whether this is high school program
 * @returns {string} The formatted training progress string
 */
export const formatTrainingProgressString = (
  completedCount,
  totalCount = 0,
  isHighSchool = false,
) => {
  if (isHighSchool) {
    return `${completedCount}`
  } else {
    return `${completedCount}/${totalCount} `
  }
}

/**
 * Gets the appropriate course tree name for training based on program type
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {string|null} The course tree name for training or null if not K8
 */
export const getTrainingCourseTreeName = (
  isK5Product = false,
  isMsProduct = false,
) => {
  if (isK5Product) {
    return K5_TRAINING_COURSE_CODE
  } else if (isMsProduct) {
    return MS_TRAINING_COURSE_CODE
  }
  return null
}

/**
 * Resolves the training progress display value for the current program.
 * Accepts either a pre-transformed `courseTree` (legacy v2 courseManager) or
 * raw `courseTreeData` from curriculum-service (V3).
 */
export function resolveTrainingProgress({
  isHighSchool,
  isK5,
  isMs,
  isDwb = false,
  highSchoolInstructorData,
  instructorData,
  courseTree,
  courseTreeData,
  trainingArticleCount = 0,
}) {
  if (isHighSchool) {
    return getCompletedTrainingProgramNodesCount(highSchoolInstructorData)
  }

  if (isK5 || isMs || isDwb) {
    const completedCount = getCompletedK8TrainingNodesCount(
      instructorData,
      isK5,
      isMs,
    )
    const trainingCourseTree =
      courseTree ??
      (courseTreeData ? transformCurriculumCourseTree(courseTreeData) : null)
    const totalCount = resolveTrainingTotalCount({
      trainingCourseTree,
      trainingArticleCount,
    })

    return formatTrainingProgressString(completedCount, totalCount, false)
  }

  return getCompletedTrainingProgramNodesCount(highSchoolInstructorData)
}
